'use client';

import { useState } from 'react';

import { AiMark } from '@/components/ai-mark';
import { Reveal } from '@/components/reveal';
import { SegmentedControl } from '@/components/segmented-control';
import { CHAT_SKINS } from '@/lib/chat-skins';
import {
  HERO_DEMO_SURFACES,
  HERO_FUNNEL_COPY,
  type HeroDemoSurface,
  type Industry,
} from '@/lib/content';
import { CARD_HOVER, cn } from '@/lib/utils';

/** Where the conversation's own cascade starts, after the columns beside it have arrived. */
const LEAD = 2;
/** Beats per line. Two, not one: a conversation that lands as fast as a list does not read as one. */
const PACE = 2;

/**
 * The client boundary for the industry panel stops here — the surface toggle is the only stateful
 * thing on the page. It only appears where the conversation happens inside an assistant; the
 * on-site industries have nothing to switch between.
 */
export function IndustryConversation({ industry }: { industry: Industry }) {
  const [surface, setSurface] = useState<HeroDemoSurface>(industry.convoIn ?? 'ChatGPT');
  const where = industry.convoIn ? `IN ${surface.toUpperCase()}` : 'ON-SITE';
  // Null for the on-site industries: that conversation happens on the brand's own site, so it
  // wears our surface rather than an assistant's.
  const skin = industry.convoIn ? CHAT_SKINS[surface] : null;

  return (
    <div className="flex flex-col gap-3.5">
      {industry.convoIn && (
        <SegmentedControl
          options={HERO_DEMO_SURFACES}
          value={surface}
          onChange={setSurface}
          scope={`industry-${industry.slug}`}
          label={HERO_FUNNEL_COPY.surfaceToggle}
          className="self-center"
          renderOption={function withMark(option) {
            return (
              <>
                <AiMark surface={option} />
                {option}
              </>
            );
          }}
        />
      )}
      <Reveal
        index={1}
        tabIndex={-1}
        className={cn('overflow-hidden rounded-2xl border bg-card', CARD_HOVER)}
      >
        <div role="group" aria-label="Illustrative conversation" className="flex flex-col">
          <div className="flex items-center justify-between border-b px-5.5 py-3">
            <span className="font-mono text-xs tracking-label text-muted-foreground">
              {industry.convoAgent} · {where}
            </span>
            <span className="font-mono text-xs tracking-label text-faint-foreground">
              ILLUSTRATIVE
            </span>
          </div>
          {/* Wearing the assistant's own canvas colours, so switching the toggle actually shows
              you the conversation happening somewhere else rather than relabelling it. Our own
              surface where it happens on the brand's site. The lines arrive one after another
              rather than as a finished block — the panel is a conversation, so it should read as
              one happening. */}
          <div
            className={cn('flex flex-col gap-3 p-5 transition-colors duration-300', skin?.panel)}
          >
            {industry.convo.map(function renderLine(line, index) {
              const beat = LEAD + index * PACE;
              return line.kind === 'tag' ? (
                <Reveal key={index} index={beat} className="flex items-center gap-3 py-1">
                  <span aria-hidden="true" className="h-px flex-1 bg-current/15" />
                  <span
                    className={cn(
                      'font-mono text-xs tracking-label whitespace-nowrap',
                      skin ? 'opacity-70' : 'text-brand',
                    )}
                  >
                    {line.text}
                  </span>
                  <span aria-hidden="true" className="h-px flex-1 bg-current/15" />
                </Reveal>
              ) : (
                <Reveal
                  key={index}
                  index={beat}
                  from={line.kind === 'user' ? 'right' : 'left'}
                  className={cn(
                    'max-w-[82%] border border-transparent px-3.5 py-2.5 text-base leading-relaxed',
                    // Same bubble shape as the hero funnel's: the tail corner is the one the
                    // speaker's side points from.
                    line.kind === 'user'
                      ? cn(
                          'self-end rounded-xl rounded-ee-[3px]',
                          skin?.bubbleUser ?? 'bg-foreground text-background',
                        )
                      : cn(
                          'self-start rounded-xl rounded-es-[3px]',
                          skin?.bubbleAgent ?? 'border-border bg-muted',
                        ),
                  )}
                >
                  {line.text}
                </Reveal>
              );
            })}
          </div>
        </div>
      </Reveal>
    </div>
  );
}
